import React, {useLayoutEffect, useRef} from 'react';
import {useCloseOnEnterSpace} from '../../hooks/input/useCloseOnEnterSpace';
import {getFactionSkinArtPath} from '../../utils/factions/getFactionSkinArtPath';
import {FACTIONS} from '../../constants/factions';
import './FactionCardOverlay.css';
import PageButton from "../elements/PageButton/PageButton";
import FactionBeastmenDescription from "./descriptions/FactionBeastmenDescription/FactionBeastmenDescription";
import FactionDemonsDescription from "./descriptions/FactionDemonsDescription/FactionDemonsDescription";
import FactionDwarvesDescription from "./descriptions/FactionDwarvesDescription/FactionDwarvesDescription";
import FactionElvesDescription from "./descriptions/FactionElvesDescription/FactionElvesDescription";
import FactionHumansDescription from "./descriptions/FactionHumansDescription/FactionHumansDescription";
import FactionInsectoidsDescription from "./descriptions/FactionInsectoidsDescription/FactionInsectoidsDescription";
import FactionLeprechaunsDescription from "./descriptions/FactionLeprechaunsDescription/FactionLeprechaunsDescription";
import FactionOrcsDescription from "./descriptions/FactionOrcsDescription/FactionOrcsDescription";
import FactionVentoryDescription from "./descriptions/FactionVentoryDescription/FactionVentoryDescription";
import {getFactionConfig} from "../../utils/factions/getFactionConfig";
import FactionElementalsDescription from "./descriptions/FactionElementalsDescription/FactionElementalsDescription";
import FactionEnlightenedDescription from "./descriptions/FactionEnlightenedDescription/FactionEnlightenedDescription";
import FactionRatmenDescription from "./descriptions/FactionRatmenDescription/FactionRatmenDescription";
import FactionGiantsDescription from "./descriptions/FactionGiantsDescription/FactionGiantsDescription";
import FactionSuccubesDescription from "./descriptions/FactionSuccubesDescription/FactionSuccubesDescription";
import FactionGoblinsDescription from "./descriptions/FactionGoblinsDescription/FactionGoblinsDescription";
import FactionCelectialsDescription from "./descriptions/FactionCelectialsDescription/FactionCelectialsDescription";
import {MENU_SOUND_MANAGER} from "../../scenes/MenuSoundManager";
import {getCityCaptureFadeProps} from "../gameUi/cityCapturePanelsFade";

const PAGE_ID = 'FactionCardOverlay';

// Сразу после появления щелчок по фону карточку не закрывает: ее часто
// открывают двойным нажатием (иконка фракции в лобби), и второй щелчок
// двойного приходится уже на фон только что открытой карточки
const BACKDROP_CLOSE_DELAY_MS = 400;

// renderActionButton — слот под действие с уже открытой фракцией (например,
// «Выбрать» в рейтинговом лобби). Отдельный от renderBuyButton: покупка
// показывается по владению фракцией, а действие — по контексту экрана, и в
// магазине они могли бы встретиться в одном ряду
const FactionCardOverlay = ({factionId, skinId, onClose, onHide, renderBuyButton, renderActionButton}) => {
  const handleHideClick = () => {
    MENU_SOUND_MANAGER.playSideMenuClick();
    if (onHide) {
      onHide();
    } else {
      onClose();
    }
  };

  useCloseOnEnterSpace(handleHideClick);

  // Когда карточка этой фракции появилась — до отрисовки, чтобы следующий же
  // щелчок уже видел отметку (см. BACKDROP_CLOSE_DELAY_MS)
  const openedAtRef = useRef(0);
  useLayoutEffect(() => {
    if (factionId) {
      openedAtRef.current = Date.now();
    }
  }, [factionId]);

  const handleBackdropClick = () => {
    if (Date.now() - openedAtRef.current < BACKDROP_CLOSE_DELAY_MS) return;
    onClose();
  };

  if (!factionId) return null;

  const config = getFactionConfig(factionId);

  // Та же формула, по которой лобби грузит арты заранее (см. lobbyArtPaths)
  const imagePath = getFactionSkinArtPath(factionId, skinId);

  let description = null;
  switch (factionId) {
    case FACTIONS.BEASTMEN:
      description = <FactionBeastmenDescription/>;
      break;
    case FACTIONS.DEMONS:
      description = <FactionDemonsDescription/>;
      break;
    case FACTIONS.DWARVES:
      description = <FactionDwarvesDescription/>;
      break;
    case FACTIONS.ELVES:
      description = <FactionElvesDescription/>;
      break;
    case FACTIONS.ENLIGHTENED:
      description = <FactionEnlightenedDescription/>;
      break;
    case FACTIONS.HUMANS:
      description = <FactionHumansDescription/>;
      break;
    case FACTIONS.INSECTOIDS:
      description = <FactionInsectoidsDescription/>;
      break;
    case FACTIONS.LEPRECHAUNS:
      description = <FactionLeprechaunsDescription/>;
      break;
    case FACTIONS.ORCS:
      description = <FactionOrcsDescription/>;
      break;
    case FACTIONS.VENTORY:
      description = <FactionVentoryDescription/>;
      break;
    case FACTIONS.ELEMENTALS:
      description = <FactionElementalsDescription/>;
      break;
    case FACTIONS.RATMEN:
      description = <FactionRatmenDescription/>;
      break;
    case FACTIONS.GIANTS:
      description = <FactionGiantsDescription/>;
      break;
    case FACTIONS.SUCCUBES:
      description = <FactionSuccubesDescription/>;
      break;
    case FACTIONS.GOBLINS:
      description = <FactionGoblinsDescription/>;
      break;
    case FACTIONS.CELECTIALS:
      description = <FactionCelectialsDescription/>;
      break;
    default:
      break;
  }

  return (
    <div className="faction-card-overlay" onClick={handleBackdropClick} {...getCityCaptureFadeProps(true)}>
      <div className="faction-card-overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className={`faction-card-overlay_card standard-background ${factionId}`}>
          <img
            className="faction-card-overlay_img"
            src={imagePath}
            alt={factionId}
          />

          <h3
            className="policy-card-overlay_policy-name"
          >
            {config.name}
          </h3>

          {description && (
            <div className={`faction-card-overlay_description ${factionId}`}>
              {description}
            </div>
          )}
        </div>

        <div className="main-menu-buttons">
          {renderBuyButton?.(config)}

          {renderActionButton?.(config)}

          <PageButton
            onClick={handleHideClick}
            noSound
            pageId={PAGE_ID}
            buttonId="hide"
            data-tutorial-select={'faction_card_close'}
          >
            Скрыть
          </PageButton>
        </div>
      </div>
    </div>
  );
};

export default FactionCardOverlay;
